import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import api from '@/services/axios'
import type { Business, Order, Stats } from '@/types'
export const useOrderStore = defineStore('order', () => {
  const orders = ref<Order[]>([])
  const order = ref<Order | null>(null)
  const ordersPagination = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0
  })
  const currentPage = ref(1)
  const isLoading = ref(false)
  const error = ref<string | null | unknown>(null)
  const payOrder = async (id: string, reference: string) => {
    try {
      const response = await api.post(`/api/customer/orders/${id}/pay`, { reference })
      return response.data
    } catch (error) {
      console.error('Error paying order:', error)
    }
  }

  const orderQuery = useQuery({
    queryKey: ['orders', currentPage],
    queryFn: async () => {
      isLoading.value = true
      error.value = null
      try {
        const response = await api.get('/api/orders', {
          params: { page: currentPage.value }
        })
        orders.value = response.data.orders
        ordersPagination.value = response.data.pagination
        isLoading.value = false
        return response.data
      } catch (err) {
        error.value = 'Failed to load orders'
        isLoading.value = false
        throw err
      }
    },
  })

  const updateOrderStatus = async (orderId: string, status: string) => {
    try {
      const response = await api.put(`/api/orders/${orderId}/status`, { status })
      order.value = response.data
      return response.data
    } catch (error) {
      console.error('Error updating order status:', error)
      throw error
    }
  }

  // Simplified pagination methods
  const fetchOrders = (page = 1) => {
    currentPage.value = page
    return orderQuery.refetch()
  }

  const nextPage = () => {
    if (currentPage.value < ordersPagination.value.last_page) {
      return fetchOrders(currentPage.value + 1)
    }
  }

  const previousPage = () => {
    if (currentPage.value > 1) {
      return fetchOrders(currentPage.value - 1)
    }
  }

  const fetchSingleOrder = async (id: string) => {
    try {
      const response = await api.get(`/api/orders/${id}`)
      order.value = response.data
      return response.data
    } catch (error) {
      console.error('Error fetching order:', error)
      throw error
    }
  }

  const getSingleOrder = (id: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return singleOrderQuery.refetch({ queryKey: ['orderItem', id] })
  }

  const singleOrderQuery = useQuery({
    queryKey: ['orderItem'],
    queryFn: () => Promise.resolve(null),
    enabled: false // Disable automatic fetching
  })


  const isError = computed(() => orderQuery.isError.value)

  return {
    orders,
    order,
    isLoading,
    isError,
    payOrder,
    fetchSingleOrder,
    updateOrderStatus,
    getSingleOrder,
    fetchOrders,
    nextPage,
    previousPage,
    currentPage,
    ordersPagination
  }
})
